import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Star, ArrowRight, Filter, Loader2 } from "lucide-react";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import ShareButton from "@/components/ShareButton";
import LanguageToggle from "@/components/LanguageToggle";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Public axios instance — no auth interceptors
const publicApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

// Fallback static seeds — guaranteed to render on fresh deployments before any
// backend data exists. Same images that are pinned in the preview DB.
const SEED_ITEMS = [
  {
    image_id: "dc714fb9",
    sim_type: "INTERIOR",
    style: "ART_DECO",
    pinned: true,
    after_url_static: "/gallery_seed/dc714fb9_after.jpg",
    before_url_static: "/gallery_seed/dc714fb9_before.jpg",
  },
  {
    image_id: "6efc3519",
    sim_type: "INTERIOR",
    style: "MODERN",
    pinned: true,
    after_url_static: "/gallery_seed/6efc3519_after.jpg",
    before_url_static: "/gallery_seed/6efc3519_before.jpg",
  },
  {
    image_id: "ae3754f2",
    sim_type: "INTERIOR",
    style: "CLASSIC",
    pinned: true,
    after_url_static: "/gallery_seed/ae3754f2_after.jpg",
    before_url_static: "/gallery_seed/ae3754f2_before.jpg",
  },
];

const STYLE_LABELS = {
  ART_DECO: "Art Deco",
  CLASSIC: "Classic",
  MODERN: "Modern",
  NEOCLASSIC: "Neoclassic",
};

const TYPE_LABELS = {
  INTERIOR: "Interior",
  EXTERIOR: "Exterior",
};

function GalleryCard({ item, onOpen }) {
  return (
    <div
      id={item.image_id}
      className="group relative block text-left rounded-xl overflow-hidden border border-white/10 bg-zinc-900/40 hover:border-orange-500/60 transition"
      data-testid={`gallery-card-${item.image_id}`}
    >
      {item.pinned && (
        <div
          className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-500/90 backdrop-blur-sm text-white text-[10px] font-bold tracking-wider uppercase shadow-lg"
          data-testid={`pinned-badge-${item.image_id}`}
        >
          <span>📌</span>
          <span>Apare pe homepage</span>
        </div>
      )}
      <button
        type="button"
        onClick={() => onOpen(item)}
        className="block w-full text-left"
      >
        <div className="aspect-[4/3] w-full bg-black overflow-hidden">
          <img
            src={
              item.after_url_static
                ? item.after_url_static
                : `${BACKEND_URL}${item.after_url}`
            }
            alt="render"
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/90 via-black/60 to-transparent">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-orange-400 font-bold mb-1">
                {TYPE_LABELS[item.sim_type]} · {STYLE_LABELS[item.style]}
              </div>
              <div className="text-xs text-white/90 flex items-center gap-1">
                <Star size={10} className="fill-orange-400 text-orange-400" />
                <Star size={10} className="fill-orange-400 text-orange-400" />
                <Star size={10} className="fill-orange-400 text-orange-400" />
                <Star size={10} className="fill-orange-400 text-orange-400" />
                <Star size={10} className="fill-orange-400 text-orange-400" />
                <span className="ml-1 text-white/60">Recomandată</span>
              </div>
            </div>
            <div className="opacity-0 group-hover:opacity-100 transition">
              <span className="inline-flex items-center gap-1 text-xs text-white font-bold bg-orange-500 px-2 py-1 rounded">
                Comparație <ArrowRight size={12} />
              </span>
            </div>
          </div>
        </div>
      </button>
      <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition">
        <ShareButton
          itemId={item.image_id}
          title={`${TYPE_LABELS[item.sim_type]} · ${STYLE_LABELS[item.style]}`}
        />
      </div>
    </div>
  );
}

export default function GalleryPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [filterType, setFilterType] = useState("ALL");
  const [filterStyle, setFilterStyle] = useState("ALL");

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType !== "ALL") params.sim_type = filterType;
      if (filterStyle !== "ALL") params.style = filterStyle;
      const { data } = await publicApi.get("/gallery/public", { params });
      const apiItems = data.items || [];
      if (apiItems.length > 0) {
        setItems(apiItems);
      } else {
        // Filter the static seeds locally based on type/style filters
        let seeds = SEED_ITEMS;
        if (filterType !== "ALL") seeds = seeds.filter((s) => s.sim_type === filterType);
        if (filterStyle !== "ALL") seeds = seeds.filter((s) => s.style === filterStyle);
        setItems(seeds);
      }
    } catch (e) {
      let seeds = SEED_ITEMS;
      if (filterType !== "ALL") seeds = seeds.filter((s) => s.sim_type === filterType);
      if (filterStyle !== "ALL") seeds = seeds.filter((s) => s.style === filterStyle);
      setItems(seeds);
    } finally {
      setLoading(false);
    }
  }, [filterType, filterStyle]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Support direct deep-links: /galerie#<image_id> → scroll to card + auto-open modal
  useEffect(() => {
    if (loading || !items.length) return;
    const hash = window.location.hash?.replace("#", "");
    if (!hash) return;
    const match = items.find((it) => it.image_id === hash);
    if (match) {
      // Delay to allow images to start loading
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        setActive(match);
      }, 400);
    }
  }, [loading, items]);

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-zinc-950/90 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="gallery-home-link">
            <div className="w-10 h-10 rounded bg-orange-500 flex items-center justify-center font-outfit font-black text-white text-sm">
              FDP
            </div>
            <div>
              <div className="font-manrope font-bold text-white text-sm tracking-wide">
                One FDP
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-orange-400">
                Fabrica de Profile
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageToggle />
            <Link to="/login">
              <Button
                variant="outline"
                className="border-white/10 text-white hover:bg-white/5"
                data-testid="gallery-login-btn"
              >
                {t("nav.login")}
              </Button>
            </Link>
            <Link to="/register">
              <Button
                className="bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="gallery-register-btn"
              >
                {t("nav.start_free")}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pt-14 pb-10">
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.35em] text-orange-400 font-bold mb-4">
          <span className="w-8 h-px bg-orange-500" />
          {t("gallery.tag")}
        </div>
        <h1 className="font-outfit font-black text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05]">
          {t("gallery.hero_title_1")} <span className="text-orange-400">{t("gallery.hero_title_2")}</span>
        </h1>
        <p className="text-zinc-400 text-base sm:text-lg mt-4 max-w-2xl">
          {t("gallery.hero_subtitle")}
        </p>
      </section>

      {/* Filters */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-6">
        <div className="flex flex-wrap items-center gap-2">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-1 mr-2">
            <Filter size={12} /> {t("gallery.filter_label")}
          </div>
          {[
            { k: "ALL", l: t("gallery.filter_all") },
            { k: "INTERIOR", l: t("gallery.filter_interior") },
            { k: "EXTERIOR", l: t("gallery.filter_exterior") },
          ].map((o) => (
            <button
              key={o.k}
              onClick={() => setFilterType(o.k)}
              className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider transition ${
                filterType === o.k
                  ? "bg-orange-500 text-white"
                  : "border border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
              }`}
              data-testid={`gallery-filter-type-${o.k.toLowerCase()}`}
            >
              {o.l}
            </button>
          ))}
          <span className="w-px h-5 bg-white/10 mx-2" />
          {[
            { k: "ALL", l: t("gallery.filter_any_style") },
            ...Object.entries(STYLE_LABELS).map(([k, l]) => ({ k, l })),
          ].map((o) => (
            <button
              key={o.k}
              onClick={() => setFilterStyle(o.k)}
              className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider transition ${
                filterStyle === o.k
                  ? "bg-orange-500 text-white"
                  : "border border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
              }`}
              data-testid={`gallery-filter-style-${o.k.toLowerCase()}`}
            >
              {o.l}
            </button>
          ))}
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-24">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-zinc-500">
            <Loader2 className="animate-spin" size={28} />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-zinc-400 text-lg mb-2">
              {t("gallery.no_results")}
            </div>
            <div className="text-zinc-600 text-sm">
              {t("gallery.no_results_hint")}{" "}
              <Link to="/register" className="text-orange-400 hover:underline">
                {t("gallery.no_results_create")}
              </Link>
              .
            </div>
          </div>
        ) : (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
            data-testid="gallery-grid"
          >
            {items.map((it) => (
              <GalleryCard key={it.image_id} item={it} onOpen={setActive} />
            ))}
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-b from-zinc-900/40 to-zinc-950 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-16 text-center">
          <h2 className="font-outfit font-black text-3xl sm:text-4xl text-white mb-3">
            {t("gallery.cta_title")}
          </h2>
          <p className="text-zinc-400 mb-6 max-w-xl mx-auto">
            {t("gallery.cta_subtitle")}
          </p>
          <Link to="/register">
            <Button
              size="lg"
              className="bg-orange-500 hover:bg-orange-600 text-white text-base px-8 py-6"
              data-testid="gallery-cta-register"
            >
              {t("nav.start_free")}
              <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Modal with slider */}
      {active && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setActive(null)}
          data-testid="gallery-modal"
        >
          <div
            className="max-w-5xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[11px] uppercase tracking-[0.3em] text-orange-400 font-bold">
                  {TYPE_LABELS[active.sim_type]} · {STYLE_LABELS[active.style]}
                </div>
                <div className="font-outfit font-bold text-white text-xl mt-1">
                  {t("gallery.comparison_title")}
                </div>
              </div>
              <button
                onClick={() => setActive(null)}
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                data-testid="gallery-modal-close"
              >
                ×
              </button>
            </div>
            <BeforeAfterSlider
              beforeSrc={
                active.before_url_static
                  ? active.before_url_static
                  : `${BACKEND_URL}${active.before_url}`
              }
              afterSrc={
                active.after_url_static
                  ? active.after_url_static
                  : `${BACKEND_URL}${active.after_url}`
              }
            />
            <p className="text-xs text-zinc-500 text-center mt-3">
              {t("gallery.comparison_hint")}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
